<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import FaqItems from "@components/FaqItems.svelte";
	import FoldingCards from "@components/FoldingCards.svelte";
	import InfoCard from "@components/InfoCard.svelte";
	import Ol from "@components/Ol.svelte";
	import { OlSize } from "@components/OlProps.js";
	import Pill from "@components/Pill.svelte";
	import { PillColor, PillSize } from "@components/PillProps.js";
	import ReferenceContents from "@components/ReferenceContents.svelte";
	import Section from "@components/Section.svelte";
	import TechnologyCards from "@components/TechnologyCards.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import { ReferencePlacement } from "@core/schema/ReferencePlacement.js";
	import { getClientsPath } from "@core/schema/paths/getClientsPath.js";
	import { getCloudSolutionsPath } from "@core/schema/paths/getCloudSolutionsPath.js";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getCrmPath } from "@core/schema/paths/getCrmPath.js";
	import { getDevelopmentPath } from "@core/schema/paths/getDevelopmentPath.js";
	import { getEcommercePath } from "@core/schema/paths/getEcommercePath.js";
	import { getEmbeddedSoftwarePath } from "@core/schema/paths/getEmbeddedSoftwarePath.js";
	import { getReferencePath } from "@core/schema/paths/getReferencePath.js";
	import { getWebAppsPath } from "@core/schema/paths/getWebAppsPath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { fastestYearsInBusiness } from "../data/fastestFounded.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getFaqsWebsites } from "../data/getFaqsWebsites.js";
	import { getReasonsForCoop } from "../data/getReasonsForCoop.js";
	import { getTechnologies } from "../data/getTechnologies.js";
	import { getTypesOfWebsites } from "../data/getTypesOfWebsites.js";
	import { getWebsitesStepByStep } from "../data/getWebsitesStepByStep.js";
	import type { WebsitesProps } from "./WebsitesProps.js";

	const { tenant } = getGlobalTemplateProps();
	const _: WebsitesProps = $props();
	const websiteFeatures = [
		"Integrace CRM, CMS a ERP",
		"Audit a optimalizace webu",
		"Automatizace marketingu",
		"Integrace platebních bran a systémů",
		"Integrace skladových řešení",
		"Zapracování marketingových a analytických nástrojů",
		"Přihlášení a autentizace uživatelů",
	];
</script>

<Layout>
	<Section background={Background.LightGradient} centered gridHeadline="container-6" gridDesc="container-6" hero>
		{#snippet headline()}
			<Formatted text="Získejte nové cg[[zákazníky]] díky rychlému a modernímu cg[[webu]] na míru" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Máme {fastestYearsInBusiness} let zkušeností s vývojem firemních webů, e-shopů nebo portálů na zakázku. sp[[Včetně administrace a integrace s dalšími systémy.]]"
				/>
			</p>
		{/snippet}
		{#snippet cta()}
			<Link
				variant={ButtonVariant.PhloxOutlineLightBg}
				class="flex items-center justify-center mx-auto mt-2"
				href={getConnectWithFastestPath(tenant)}
			>
				<Formatted text="Poptat webovky na míru" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
	</Section>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		paddingFollowNext
	>
		{#snippet headline()}
			<Formatted text="Vysoce výkonný web 100% podle cg[[vašich přání]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Stručná webová prezentace, vzdělávací portál nebo e-shop s desítkami stránek? Na tom nesejde, sp[[ve Fastest pro vás vytvoříme jakékoliv webovky [{getDevelopmentPath(
						tenant,
					)}][[na míru,]]]] sp[[včetně administrace (CMS)]]. Design i funkce do puntíku sladíme s potřebami vašeho podnikání. Stránky tvoříme tak, aby byly rychlé, výkonné a měli je rádi návštěvníci i vyhledávače."
				/>
			</p>
			<p>
				<Formatted
					text="Hotové webovky, [{getEcommercePath(
						tenant,
					)}][[e-commerce řešení]] nebo portál sp[[zvládneme integrovat]] s vaším [{getCrmPath(
						tenant,
					)}][[CRM]], ERP, [{getWebAppsPath(
						tenant,
					)}][[webovou]] či mobilní aplikací a dalšími klíčovými systémy. Zajistíme, aby stránky nebyly pátým kolem u vozu a dokonale zapadaly do firemních procesů."
				/>
			</p>
			<p>
				<Formatted text="Weby vyvíjíme primárně na sp[[cloudu AWS (Amazon Web Services).]]" />
			</p>
		{/snippet}
	</Section>

	<Section background={Background.LightGradient} noPadding="bot" gridHeadline="container-6 lg:text-center" centered>
		{#snippet headline()}
			<Formatted text="Jaká webová řešení cg[[tvoříme]]?" />
		{/snippet}
	</Section>
	<Section gridChildren="max-md:full-width" background={Background.White} noPadding="both">
		<div
			class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-1 max-lg:full-width lg:px-1 bg-fstlight-100 border-t-4 border-fstlight-100"
		>
			{#each getTypesOfWebsites(tenant) as { title, desc }, index (index)}
				<InfoCard {title} {desc} />
			{/each}
		</div>
	</Section>
	<Link
		variant={ButtonVariant.Phlox}
		class="flex items-center justify-center mx-auto mt-8 lg:mt-12"
		href={getConnectWithFastestPath(tenant)}
	>
		<Formatted text="Zeptat se na podrobnosti" />
		<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
	</Link>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		paddingFollowNext
	>
		{#snippet headline()}
			<Formatted text="Postaráme se o cg[[integraci]] systémů i cg[[zapracování]] klíčových funkcí" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="sp[[Web by měl fungovat v souladu s dalšími částmi vašeho podnikání.]] Proto se postaráme o jeho integraci s firemními systémy nebo do něj zapracujeme funkce, které vám usnadní každodenní administrativu."
				/>
			</p>
			<div class="flex gap-1 sm:gap-2 flex-wrap">
				{#each websiteFeatures as label (label)}
					<Pill size={PillSize.Small} color={PillColor.White}>{label}</Pill>
				{/each}
			</div>
		{/snippet}
	</Section>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		paddingFollowNext
	>
		{#snippet headline()}
			<Formatted text="Umíme i vývoj cg[[nad]] rámec webových stránek" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Potřebujete vytvořit [{getCloudSolutionsPath(tenant)}][[aplikaci v cloudu]], [{getEmbeddedSoftwarePath(
						tenant,
					)}][[embedded software]] na míru nebo převzít kontrolu nad interním projektem? sp[[Postaráme se o to, máme přesah nad rámec vývoje webů.]] "
				/>
			</p>
		{/snippet}
	</Section>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="Náš cg[[proces]] tvorby webu" />
			<span class="block text-2md font-bold mt-6">
				<Formatted
					text="Harmonogram vytváření webu stavíme na míru cg[[potřebám]] a cg[[možnostem]] každého klienta. "
				/>
			</span>
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={getWebsitesStepByStep(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}
		<Link
			variant={ButtonVariant.Phlox}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
			><Formatted text="Rezervovat online schůzku" />
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section
		gridHeadline="container-6"
		gridDesc="container-6"
		background={Background.LightGradient}
		paddingFollowNext
		centered
	>
		{#snippet headline()}
			<Formatted text="Spoléháme na cg[[moderní]] technologie a vyvíjíme na cg[[cloudu]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted text="Pracujeme s ověřenými technologiemi, které nabízí ideální poměr sp[[cena/výkon.]]" />
			</p>
		{/snippet}
	</Section>
	<TechnologyCards technologies={getTechnologies(tenant)} />
	<Link
		variant={ButtonVariant.Phlox}
		class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
		href={getConnectWithFastestPath(tenant)}
		>Chci fastest služby
		<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
	</Link>

	<Section id="reference" gridHeadline="container-8" centered>
		{#snippet headline()}
			<Formatted text="cg[[Kdo]] patří mezi naše cg[[spokojené]] cg[[e-commerce]] zákazníky?" />
		{/snippet}
		<div class="mt-12">
			<ReferenceContents limit={8} placement={ReferencePlacement.WebsEshops} />
		</div>
		<Link
			variant={ButtonVariant.GreenOutlineLightBg}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getReferencePath(tenant)}
			>Zobrazit více
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section centered gridHeadline="container-6">
		{#snippet headline()}
			<Formatted text="Často kladené otázky k cg[[tvorbě webu]]" />
		{/snippet}
		<FaqItems faqs={getFaqsWebsites(tenant)} />
	</Section>

	<Section
		background={Background.Onyx}
		gridHeadline="container-6 lg:container-start-1 lg:container-end-5 xl:container-start-2 xl:container-end-4 max-lg:text-center"
		gridDesc="lg:container-start-7 lg:container-end-12 xl:container-start-6 xl:container-end-11"
	>
		{#snippet headline()}
			<Formatted text="cg[[Proč]] si plácnout s cg[[Fastest Solution]]" />
		{/snippet}
		{#snippet desc()}
			<FoldingCards color={Background.Onyx} cards={getReasonsForCoop(tenant)} />
		{/snippet}
		<div class="container-12">
			<Link
				variant={ButtonVariant.Phlox}
				class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
				href={getConnectWithFastestPath(tenant)}
				>Chci fastest služby
				<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
			</Link>
		</div>
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera]]} />
</Layout>
